import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useNotificationContext } from 'ui/components/Notification'
import { BarcodeListener, BarcodeListenerProps } from './BarcodeListener'
import { ScannedBarcodeToast } from './ScannedBarcodeToast'
import { noop } from 'lodash'

/**
 * We've got a global barcode listener which is included in the App.js file.
 * This looks out for scanned barcodes and shows a toast in the corner
 * when it detects one that's been scanned.
 *
 * There are certain scenarios when we _don't_ want the global barcode listener
 * to be listening. In those cases, we can use this context to disable it.
 * Don't forget to set it back to active after you're done.
 *
 * Usage example:
 *
 * ```
 * const { setActive } = useGlobalBarcodeListenerContext()
 *
 * useEffect(() => {
 *  setActive(false)
 *
 *  return () => {
 *   setActive(true)
 * })
 * ```
 *
 * If you want to listen to barcodes but you don't want to invoke the default behaviour,
 * for example if you're associating a barcode to a client and you don't want to trigger
 * the toast when a barcode is scanned, you can change the callback using `setCallback`.
 * Don't forget to set it back to the global listener when you're done.
 *
 * Usage example:
 *
 * ```
 * const { setCallback, reset } = useGlobalBarcodeListenerContext()
 *
 * useEffect(() => {
 *   setCallback(doSomethingElseWithBarcode)
 *
 *   return () => {
 *     reset()
 *   }
 * })
 *
 */
interface Context {
  active: boolean
  setActive: Dispatch<SetStateAction<boolean>>
  prefixes: string[]
  suffixes: string[]
}

export const GlobalBarcodeListenerContext = createContext<Context>({
  active: true,
  setActive: noop,
  prefixes: [],
  suffixes: [],
})

interface Props {
  children: ReactNode
}

export const GlobalBarcodeListenerProvider: FC<Props> = ({ children }) => {
  const [active, setActive] = useState(true)
  const notification = useNotificationContext()
  const prefixes = useMemo<string[]>(() => [], [])
  const suffixes = useMemo<string[]>(() => [], [])

  const handleBarcodeScan: BarcodeListenerProps['onScan'] = (code) => {
    notification.notify(<ScannedBarcodeToast key={code + Date.now()} code={code} />, {
      key: 'barcode-scan',
    })
  }

  const context = useMemo(
    () => ({ active, setActive, prefixes, suffixes }),
    [active, prefixes, suffixes]
  )

  return (
    <GlobalBarcodeListenerContext.Provider value={context}>
      {children}
      <BarcodeListener
        active={active}
        prefixCodes={prefixes}
        suffixCodes={suffixes}
        onScan={handleBarcodeScan}
      />
    </GlobalBarcodeListenerContext.Provider>
  )
}

export const useGlobalBarcodeListenerContext = () => useContext(GlobalBarcodeListenerContext)
